/* Take A Break America theme script. Each part only runs if its element is on the page. */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');

  /* Fatigue field: lines tighten and slow where load builds, relax at breaks.
     The cursor acts as an extra break. Decorative only. */
  const fatigueField = (canvas) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const LINES = 26, STEP = 8, breakWidth = 0.16;
    let w = 0, h = 0, t = 0, last = 0, raf = 0, visible = true;
    let mouseX = -1e4, mouseY = -1e4, scrollShift = 0, rgb = '243, 242, 238';

    const readColors = () => {
      const cs = getComputedStyle(document.documentElement);
      rgb = cs.getPropertyValue('--field').trim() || rgb;
    };
    const resize = () => {
      const r = canvas.getBoundingClientRect(), dpr = Math.min(devicePixelRatio || 1, 2);
      w = r.width; h = r.height;
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const period = () => Math.max(260, w / 3.2);
    const load = (x) => {
      const p = period();
      const u = ((((x + scrollShift) % p) + p) % p) / p;
      let l = u < 1 - breakWidth ? Math.pow(u / (1 - breakWidth), 1.6) : 0;
      if (u > 1 - breakWidth - 0.04 && u < 1 - breakWidth) l *= (1 - breakWidth - u) / 0.04;
      const d = Math.abs(x - mouseX);
      if (d < 140 && mouseY > -1e3) l *= d / 140;
      return l;
    };
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const top = h * 0.12, bottom = h * 0.9, mid = (top + bottom) / 2, gap = (bottom - top) / (LINES - 1);
      const loads = [];
      for (let x = 0; x <= w + STEP; x += STEP) loads.push(load(x));
      const grad = ctx.createLinearGradient(0, 0, w, 0);
      grad.addColorStop(0, `rgba(${rgb}, 0.02)`);
      grad.addColorStop(0.45, `rgba(${rgb}, 0.07)`);
      grad.addColorStop(1, `rgba(${rgb}, 0.20)`);
      ctx.strokeStyle = grad; ctx.lineWidth = 1;
      for (let i = 0; i < LINES; i++) {
        const base = top + i * gap;
        ctx.beginPath();
        for (let x = 0, j = 0; x <= w + STEP; x += STEP, j++) {
          const l = loads[j];
          const y = mid + (base - mid) * (1 - 0.62 * l) + Math.sin(x * 0.012 + i * 0.5 + t * (0.35 + 0.65 * (1 - l))) * (1.2 + 4.5 * (1 - l));
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
    };
    const frame = (now) => {
      raf = 0;
      if (!visible || document.hidden || reduce.matches) return;
      if (now - last > 33) { t += (Math.min(now - last, 100) / 1000) * 0.9; last = now; draw(); }
      raf = requestAnimationFrame(frame);
    };
    const start = () => { if (!raf && !reduce.matches) { last = performance.now(); raf = requestAnimationFrame(frame); } };

    readColors(); resize(); draw();
    new ResizeObserver(() => { resize(); draw(); }).observe(canvas);
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) start(); }).observe(canvas);
    document.addEventListener('visibilitychange', start);
    reduce.addEventListener('change', () => { draw(); start(); });
    const hero = canvas.parentElement;
    hero.addEventListener('pointermove', (e) => {
      if (reduce.matches) return;
      const r = canvas.getBoundingClientRect();
      mouseX = e.clientX - r.left; mouseY = e.clientY - r.top;
    });
    hero.addEventListener('pointerleave', () => { mouseX = mouseY = -1e4; });
    addEventListener('scroll', () => { if (!reduce.matches) scrollShift = scrollY * 0.15; }, { passive: true });
    start();
  };

  /* Counters count up once when scrolled into view. */
  const counters = (els) => {
    if (reduce.matches || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        const el = entry.target, end = Number(el.dataset.count), t0 = performance.now();
        const tick = (now) => {
          const p = Math.min((now - t0) / 1400, 1);
          el.textContent = String(Math.round(end * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.6 });
    els.forEach((el) => { if (el.getBoundingClientRect().top > innerHeight) el.textContent = '0'; io.observe(el); });
  };

  /* Soft rise-in as content enters the screen. Content already on screen shows at once. */
  const reveal = (els) => {
    if (reduce.matches || !('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < innerHeight && r.bottom > 0) requestAnimationFrame(() => el.classList.add('is-in')); else io.observe(el);
    });
  };

  /* Wrap each word of a heading so the words can rise in one by one. Keeps inner spans (the colored part). */
  const splitWords = (el) => {
    let i = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 3) {
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.append(part); return; }
            const w = document.createElement('span'), inner = document.createElement('span');
            w.className = 'w'; inner.style.setProperty('--i', i++); inner.textContent = part;
            w.append(inner); frag.append(w);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1) walk(child);
      });
    };
    el.setAttribute('aria-label', el.textContent.trim());
    walk(el);
    el.querySelectorAll('.w').forEach((w) => w.setAttribute('aria-hidden', 'true'));
    requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-in')));
  };

  /* Quote words light up in reading order as the quote scrolls through the middle of the screen. */
  const litWords = (el) => {
    const text = el.textContent;
    el.setAttribute('aria-label', text.trim());
    el.innerHTML = '';
    const words = text.split(/(\s+)/).map((part) => {
      if (/^\s*$/.test(part)) { el.append(part); return null; }
      const span = document.createElement('span'); span.className = 'q'; span.setAttribute('aria-hidden', 'true');
      span.textContent = part; el.append(span); return span;
    }).filter(Boolean);
    let ticking = false;
    const update = () => {
      ticking = false;
      const r = el.getBoundingClientRect();
      const p = Math.min(Math.max((innerHeight * 0.8 - r.top) / (r.height + innerHeight * 0.35), 0), 1);
      const n = Math.round(p * words.length);
      words.forEach((w, k) => w.classList.toggle('is-lit', k < n));
    };
    addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
  };

  /* Header turns frosted once the page scrolls. */
  const header = (el) => {
    const update = () => el.classList.toggle('is-scrolled', scrollY > 10);
    addEventListener('scroll', update, { passive: true });
    update();
  };

  /* Inquiry form: accessible client-side checks, then a normal Shopify submit. */
  const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
  const inquiry = (form) => {
    const required = Array.from(form.querySelectorAll('input[required]'));
    if (!required.length) { // already submitted: move focus to the confirmation
      const done = form.querySelector('.confirm');
      if (done && location.search.includes('contact_posted')) done.focus();
      return;
    }
    form.noValidate = true;
    const status = form.querySelector('.js-status');
    const msg = form.querySelector('textarea');
    const count = msg && document.getElementById(msg.getAttribute('aria-describedby'));
    msg && msg.addEventListener('input', () => { if (count) count.textContent = `${msg.value.length} / 500`; });

    const check = (el) => {
      const bad = el.type === 'checkbox' ? !el.checked : el.type === 'email' ? !emailOk(el.value.trim()) : !el.value.trim();
      const err = document.getElementById(el.getAttribute('aria-describedby'));
      el.setAttribute('aria-invalid', String(bad));
      if (err) { err.hidden = !bad; err.textContent = bad ? el.dataset.msg : ''; }
      return !bad;
    };
    required.forEach((el) => el.addEventListener(el.type === 'checkbox' ? 'change' : 'blur', () => {
      if (el.hasAttribute('aria-invalid')) check(el);
    }));
    form.addEventListener('submit', (e) => {
      const invalid = required.filter((el) => !check(el));
      if (!invalid.length) { if (status) status.textContent = 'Sending…'; return; }
      e.preventDefault();
      if (status) status.textContent = `Please fix ${invalid.length} ${invalid.length === 1 ? 'field' : 'fields'}.`;
      invalid[0].focus();
    });
  };

  /* Essay: table of contents from the article's h2s, current-section highlight, reading progress. */
  const essay = (article) => {
    const bar = document.querySelector('[data-progress]');
    const toc = document.querySelector('[data-toc]');
    const headings = Array.from(article.querySelectorAll('h2'));
    const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const links = [];
    headings.forEach((h) => {
      const text = h.textContent.trim();
      if (!h.id) h.id = slug(text);
      if (/^references$/i.test(text)) h.classList.add('is-refs');
      if (toc) {
        const li = document.createElement('li'), a = document.createElement('a');
        a.href = `#${h.id}`; a.textContent = text;
        li.append(a); toc.querySelector('ol').append(li); links.push(a);
      }
    });
    if (toc && links.length) toc.hidden = false;

    let ticking = false;
    const update = () => {
      ticking = false;
      if (bar) {
        const r = article.getBoundingClientRect(), total = r.height - innerHeight;
        bar.style.transform = `scaleX(${total > 0 ? Math.min(Math.max(-r.top / total, 0), 1) : 1})`;
      }
      let current = '';
      for (const h of headings) if (h.getBoundingClientRect().top < innerHeight * 0.3) current = h.id;
      links.forEach((a) => (a.hash === `#${current}` ? a.setAttribute('aria-current', 'true') : a.removeAttribute('aria-current')));
    };
    addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
  };

  const init = () => {
    document.querySelectorAll('[data-fatigue-field]').forEach(fatigueField);
    const c = document.querySelectorAll('[data-count]'); if (c.length) counters(c);
    const head = document.querySelector('.site-header'); if (head) header(head);
    if (!reduce.matches) document.querySelectorAll('[data-split]').forEach(splitWords);
    document.querySelectorAll('[data-words]').forEach(litWords);
    reveal(document.querySelectorAll('[data-reveal]'));
    document.querySelectorAll('form.js-inquiry').forEach(inquiry);
    const a = document.querySelector('[data-essay]'); if (a) essay(a);
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
})();
